/* Stable availability groups across the complete native Shopify result set.
 * Source cards, variants, prices and purchase guards remain server-owned. */
(() => {
  'use strict';
  const MAX_ITEMS = 25000;
  const stablePartition = rows => rows.filter(row => row.type !== 'product' || row.available === true)
    .concat(rows.filter(row => row.type === 'product' && row.available !== true));
  function validatePages(pages) {
    if (!pages.length) throw new Error('missing-index');
    const first = pages[0].meta;
    if (first.version !== 1 || !Number.isInteger(first.total) || first.total < 0 || first.total > MAX_ITEMS ||
        !Number.isInteger(first.pageSize) || first.pageSize < 1 || first.pageSize > 250 ||
        first.pages !== Math.max(1, Math.ceil(first.total / first.pageSize))) throw new Error('unsupported-index');
    if (pages.length !== first.pages) throw new Error('incomplete-index');
    const seenPages = new Set(), keys = new Set(), identities = new Set(), rows = [];
    pages.sort((a, b) => a.meta.page - b.meta.page).forEach(({ meta, cards }) => {
      if (meta.total !== first.total || meta.pageSize !== first.pageSize || meta.pages !== first.pages ||
          meta.context !== first.context || !Number.isInteger(meta.page) || meta.page < 1 || meta.page > first.pages ||
          seenPages.has(meta.page) || !Array.isArray(meta.rows)) throw new Error('index-changed');
      seenPages.add(meta.page);
      const expected = Math.min(first.pageSize, Math.max(0, first.total - (meta.page - 1) * first.pageSize));
      if (meta.rows.length !== expected) throw new Error('incomplete-page');
      for (const row of meta.rows) {
        if (!row || !/^(product|article|page):\d+(?::\d+)?$/.test(row.key || '') ||
            !['product', 'article', 'page'].includes(row.type) || typeof row.available !== 'boolean' ||
            typeof row.eligible !== 'boolean' || !row.key.startsWith(row.type + ':') || keys.has(row.key)) throw new Error('invalid-identity');
        const identity = row.key.split(':').slice(0, 2).join(':');
        if (identities.has(identity)) throw new Error('duplicate-resource');
        identities.add(identity);
        if (row.eligible && !cards.has(row.key)) throw new Error('missing-source-card');
        keys.add(row.key); rows.push(row);
      }
    });
    if (rows.length !== first.total) throw new Error('incomplete-coverage');
    return rows;
  }
  function sourceURL(base, current, section, pageParam, page) {
    const here = new URL(current), url = new URL(base, here);
    if (url.origin !== here.origin || !/^https?:$/.test(url.protocol)) throw new Error('invalid-source-url');
    // All repeated facet values, vendor/type q, locale, market and preview values survive.
    if (!url.search) url.search = here.search;
    url.searchParams.delete('sections'); url.searchParams.delete('section_id');
    url.searchParams.set(pageParam || 'page', String(page));
    url.searchParams.set('section_id', section);
    return url;
  }
  const api = { stablePartition, validatePages, sourceURL, MAX_ITEMS };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('cn-availability-list')) return;

  class AvailabilityList extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true; this.run = 0; this.filter = null;
      this.addEventListener('cn:order-filter', event => {
        if (event.target === this && typeof event.detail?.predicate === 'function') this.cnOrderSetFilter(event.detail.predicate, event.detail);
      });
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-cn-order-page-link]');
        if (!link || !this.contains(link) || !this.rows || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); this.render(Number(link.dataset.cnOrderPageLink), true);
        this.querySelector('[data-cn-order-items]')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      });
      this.historyListener = () => { if (this.rows && !this.dataset.cnLimit) this.render(this.requestedPage(), false); };
      window.addEventListener('popstate', this.historyListener);
      queueMicrotask(() => { if (this.isConnected) this.start(); });
    }
    disconnectedCallback() {
      this.run++; this.controller?.abort(); window.removeEventListener('popstate', this.historyListener);
      this.started = false;
    }
    requestedPage() {
      const value = Number(new URL(location.href).searchParams.get(this.dataset.cnPageParam || 'page') || 1);
      return Number.isSafeInteger(value) && value > 0 ? value : 1;
    }
    cnOrderSetFilter(predicate, options = {}) {
      if (predicate !== null && typeof predicate !== 'function') throw new TypeError('A row predicate is required');
      this.filter = predicate;
      if (this.rows) this.render(options.page || 1, false);
    }
    notice(text, retry = false) {
      let node = this.querySelector('[data-cn-order-notice]');
      if (!node) { node = document.createElement('p'); node.dataset.cnOrderNotice = ''; node.className = 'cn-order-notice'; node.setAttribute('role', 'status'); this.prepend(node); }
      node.replaceChildren(document.createTextNode(text));
      if (retry) {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = 'Erneut versuchen';
        button.addEventListener('click', () => this.start()); node.append(' ', button);
      }
    }
    parse(root) {
      const script = root.querySelector('script[data-cn-order-page]');
      if (!script) throw new Error('missing-index');
      const meta = JSON.parse(script.textContent);
      const source = root.querySelector('template[data-cn-order-source]')?.content || root.querySelector('[data-cn-order-items]');
      if (!source) throw new Error('missing-card-source');
      const cards = new Map();
      [...source.children].filter(node => node.hasAttribute('data-cn-order-key')).forEach(node => {
        const key = node.dataset.cnOrderKey;
        if (cards.has(key)) throw new Error('duplicate-source-card');
        cards.set(key, node);
      });
      return { meta, cards };
    }
    async fetchPage(page, signal) {
      const url = sourceURL(this.dataset.cnSourceUrl || location.pathname, location.href,
        this.dataset.cnSourceSection, this.dataset.cnPageParam || 'page', page);
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('source-request-failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const host = [...doc.querySelectorAll('cn-availability-list')].find(node =>
        node.dataset.cnSourceSection === this.dataset.cnSourceSection && node.dataset.cnListKind === this.dataset.cnListKind);
      if (!host) throw new Error('source-section-missing');
      const result = this.parse(host);
      if (result.meta.page !== page) throw new Error('wrong-source-page');
      return result;
    }
    async start() {
      const run = ++this.run; this.controller?.abort(); this.controller = new AbortController();
      const controller = this.controller, timer = setTimeout(() => controller.abort(), 180000);
      this.dataset.cnOrderState = 'pending'; this.notice('Verfügbare Produkte werden für die gesamte Auswahl geordnet …');
      try {
        const initial = this.parse(this), m = initial.meta;
        if (typeof m.pageParam === 'string' && m.pageParam) this.dataset.cnPageParam = m.pageParam;
        if (m.version !== 1 || !Number.isInteger(m.pages) || m.pages < 1 || m.total > MAX_ITEMS ||
            m.pages !== Math.max(1, Math.ceil(m.total / m.pageSize))) throw new Error('unsupported-index');
        const pages = [initial], pending = Array.from({ length: m.pages }, (_, i) => i + 1).filter(page => page !== m.page);
        let done = 1;
        const worker = async () => {
          while (pending.length) {
            const page = pending.shift(); pages.push(await this.fetchPage(page, controller.signal));
            if (run !== this.run || !this.isConnected) return;
            done++; this.notice(`Produktauswahl wird geordnet (${done} von ${m.pages} Quellseiten) …`);
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, pending.length) }, worker));
        if (run !== this.run || !this.isConnected) return;
        const rows = validatePages(pages);
        this.cards = new Map(); pages.forEach(page => page.cards.forEach((node, key) => this.cards.set(key, node)));
        this.rows = rows.map(row => Object.freeze({ ...row, ...(row.offerFilter ? { offerFilter: Object.freeze({ ...row.offerFilter }) } : {}) }));
        this.querySelector('[data-cn-order-notice]')?.remove();
        this.dataset.cnOrderState = 'ready'; this.dataset.cnOrderCoverage = String(rows.length);
        this.dispatchEvent(new CustomEvent('cn:order-index', { bubbles: true,
          detail: { entries: this.rows.filter(row => row.eligible), total: rows.filter(row => row.eligible).length, sourceTotal: rows.length, sourceUrl: this.dataset.cnSourceUrl || location.pathname } }));
        this.render(this.requestedPage(), false);
      } catch (error) {
        if (run !== this.run || !this.isConnected) return;
        controller.abort(); this.rows = null; this.cards = null;
        this.dataset.cnOrderState = 'fallback'; delete this.dataset.cnOrderCoverage;
        this.notice('Die vollständige Reihenfolge konnte nicht geladen werden. Angezeigt wird die ursprüngliche Shopify-Auswahl. ', true);
        this.dispatchEvent(new CustomEvent('cn:order-error', { bubbles: true, detail: { reason: error.message || error.name } }));
      } finally { clearTimeout(timer); }
    }
    render(requested, updateHistory) {
      if (!this.rows || !this.isConnected) return;
      let eligible;
      try { eligible = this.rows.filter(row => row.eligible && (!this.filter || this.filter(row))); }
      catch { this.notice('Der Filter konnte nicht angewendet werden. Bitte prüfe deine Auswahl.'); return; }
      const ordered = stablePartition(eligible), size = Number(this.dataset.cnLimit || this.dataset.cnPageSize);
      if (!Number.isSafeInteger(size) || size < 1) return;
      const pages = this.dataset.cnLimit ? 1 : Math.max(1, Math.ceil(ordered.length / size));
      const page = Math.min(pages, Math.max(1, requested || 1)), visible = ordered.slice((page - 1) * size, page * size);
      const target = this.querySelector('[data-cn-order-items]'); if (!target) return;
      const nodes = visible.map((row, i) => {
        const card = this.cards.get(row.key).cloneNode(true);
        if (card.id?.startsWith('Slide-')) card.id = `Slide-${this.dataset.cnSourceSection}-${i + 1}`;
        if (card.hasAttribute('data-cascade')) card.style.setProperty('--animation-order', String(i + 1));
        card.classList.remove('scroll-trigger'); return card;
      });
      target.replaceChildren(...nodes);
      let empty = this.querySelector('[data-cn-order-empty]');
      if (!visible.length) {
        if (!empty) { empty = document.createElement('p'); empty.dataset.cnOrderEmpty = ''; empty.className = 'cn-order-notice'; empty.textContent = 'Für diese Auswahl sind gerade keine Produkte vorhanden.'; target.after(empty); }
      } else empty?.remove();
      const pagination = this.querySelector('[data-cn-order-pagination]');
      if (pagination) {
        pagination.replaceChildren(); pagination.hidden = pages <= 1;
        if (pages > 1) {
          const nav = document.createElement('nav'); nav.className = 'cn-order-pagination'; nav.setAttribute('aria-label', 'Produktseiten');
          const numbers = [...new Set([1, page - 1, page, page + 1, pages].filter(n => n >= 1 && n <= pages))];
          if (page > 1) nav.append(this.pageLink(page - 1, 'Zurück'));
          let previous = 0;
          numbers.forEach(n => { if (previous && n > previous + 1) nav.append(' … '); const link = this.pageLink(n, String(n)); if (n === page) link.setAttribute('aria-current', 'page'); nav.append(link); previous = n; });
          if (page < pages) nav.append(this.pageLink(page + 1, 'Weiter')); pagination.append(nav);
        }
      }
      if (updateHistory) { const url = new URL(location.href); url.searchParams.set(this.dataset.cnPageParam || 'page', String(page)); history.pushState({ ...(history.state || {}), cnOrderPage: page }, '', url); }
      this.dataset.cnOrderPage = String(page);
      this.closest('slider-component')?.resetPages?.(); this.querySelector('slider-component')?.resetPages?.();
      document.dispatchEvent(new CustomEvent('cn:products-updated'));
      this.dispatchEvent(new CustomEvent('cn:order-render', { bubbles: true, detail: { entries: visible, page, pages, total: ordered.length } }));
    }
    pageLink(page, label) {
      const link = document.createElement('a'), url = new URL(location.href);
      url.searchParams.set(this.dataset.cnPageParam || 'page', String(page));
      link.href = url.href; link.textContent = label; link.dataset.cnOrderPageLink = String(page); return link;
    }
  }
  customElements.define('cn-availability-list', AvailabilityList);
})();
